import { PushBuilderData } from "pages/PushBuilder/PushBuilderContent";
import {
  ComparisonType,
  ConditionalSegmentsSettings,
  GeneralSelectedType,
  MessageEmailEventCondition,
  MessageInAPPEventCondition,
  MessagePushEventCondition,
  MessageSMSEventCondition,
  StatementValueType,
} from "reducers/flow-builder.reducer";
import { PushPlatforms } from "../../PushBuilder/PushBuilderContent";
import { MessageType, ProviderType } from "types/Workflow";
import { NodeType } from "../FlowEditor";

export enum BranchType {
  EVENT = "event",
  MAX_TIME = "maxTime",
  MULTISPLIT = "multisplit",
  EXPERIMENT = "experiment",
  RESPONSE = "response",
}

export type ResponseMatchType = "keyword" | "contains" | "regex";

/**
 * Top-level routing strategy for a Response Branch node.
 *  - "ai":      one AI classification call across all branches' prompts
 *  - "keyword": per-branch matchType/matchValue rules (the original behavior)
 * Old saved nodes have no routingMode and are treated as "keyword".
 */
export type ResponseRoutingMode = "ai" | "keyword";

export enum EventType {
  ANALYTICS = "analytics",
  MESSAGE = "message",
  ATTRIBUTE_CHANGE = "attribute_change",
}

export enum LogicRelation {
  AND = "and",
  OR = "or",
}

export enum StatementType {
  PROPERTY = "property",
  ELEMENT = "element",
}

export interface PropertyStatement {
  type: StatementType.PROPERTY;
  key: string;
  comparisonType: ComparisonType;
  valueType: StatementValueType;
  value: string;
  relationToNext: LogicRelation;
}

export enum ElementKey {
  TAG_NAME = "tagName",
  TEXT = "text",
}

export interface ElementStatement {
  type: StatementType.ELEMENT;
  order: number;
  elementKey: ElementKey;
  comparisonType: ComparisonType;
  valueType: StatementValueType;
  value: string;
  relationToNext: LogicRelation;
}

export type Statement = PropertyStatement | ElementStatement;

export interface CommonCondition {
  relationToNext: LogicRelation;
}

export interface TrackerCondition extends CommonCondition {
  providerType: ProviderType.TRACKER;
  trackerId?: string;
  event?: string;
}

export interface HitCondition extends CommonCondition {
  type: EventType;
  providerType: Exclude<ProviderType, ProviderType.TRACKER>;
  name: string;
  statements: Statement[];
}

export enum WUAttributeHappenCondition {
  CHANGED = "changed",
  CHANGED_TO = "changed to",
}

export interface WUAttributeCondition extends CommonCondition {
  providerType: ProviderType.WU_ATTRIBUTE;
  attributeName: string;
  happenCondition: WUAttributeHappenCondition;
  valueType: Exclude<
    StatementValueType,
    StatementValueType.ARRAY | StatementValueType.OBJECT
  >;
  value: string;
  type: EventType;
}

export interface MessageCondition extends CommonCondition {
  providerType:
    | ProviderType.EMAIL_MESSAGE
    | ProviderType.SMS_MESSAGE
    | ProviderType.IN_APP_MESSAGE
    | ProviderType.PUSH_MESSAGE;
  from?: GeneralSelectedType;
  eventCondition:
    | MessageEmailEventCondition
    | MessagePushEventCondition
    | MessageSMSEventCondition
    | MessageInAPPEventCondition;
  fromSpecificMessage: GeneralSelectedType;
  type: EventType;
}

export type Condition =
  | HitCondition
  | TrackerCondition
  | MessageCondition
  | WUAttributeCondition;

export interface CommonBranch {
  id: string;
}

export interface EventBranch extends CommonBranch {
  type: BranchType.EVENT;
  conditions: Condition[];
}

export interface MultisplitBranch extends CommonBranch {
  type: BranchType.MULTISPLIT;
  conditions?: ConditionalSegmentsSettings;
  isOthers?: boolean;
}

export interface ExperimentBranch extends CommonBranch {
  type: BranchType.EXPERIMENT;
  ratio: number;
  label?: string;
}

export interface ExperimentAutoWinnerSettings {
  enabled?: boolean;
  metric?: "positive_rate" | "positive_count";
  minAssignments?: number;
  targetPositive?: number;
}

export interface ResponseBranch extends CommonBranch {
  type: BranchType.RESPONSE;
  /** Short label shown in UI; also handed to AI as a classification option */
  label: string;
  matchType: ResponseMatchType;
  matchValue: string;
  caseSensitive?: boolean;
  /**
   * Free-text description of the kind of reply that belongs on this branch.
   * Used by the AI classifier when the parent node's routingMode is "ai".
   */
  prompt?: string;
  /** Catch-all "any other reply" branch; mutually exclusive with the matcher fields */
  isOthers?: boolean;
  /** Timeout fallback branch ("no reply within ..."); mutually exclusive with the matcher fields */
  isTimeout?: boolean;
  delay?: DelayData;
}

export enum TimeType {
  TIME_DELAY = "timeDelay",
  TIME_WINDOW = "timeWindow",
}

export interface CommonMaxTimeBranch extends CommonBranch {
  type: BranchType.MAX_TIME;
}

export interface DelayData {
  days: number;
  hours: number;
  minutes: number;
  seconds?: number;
  businessDays?: number;
  businessHoursOnly?: boolean;
  timezone?: string;
  businessHours?: {
    daysOfWeek?: number[];
    startTime?: string;
    endTime?: string;
  };
}

export interface TimeDelayBranch extends CommonMaxTimeBranch {
  timeType: TimeType.TIME_DELAY;
  delay: DelayData;
}

export interface TimeWindowBranch extends CommonMaxTimeBranch {
  timeType: TimeType.TIME_WINDOW;
  waitFrom?: string;
  waitTo?: string;
}

export type MaxTimeBranch = TimeDelayBranch | TimeWindowBranch;

export type WaitUntilBranch = EventBranch | MaxTimeBranch;

export interface AttributeStatement {
  key: string;
  comparisonType: ComparisonType;
  valueType: StatementValueType;
  relationToNext: LogicRelation;
  value: string;
}

export interface AttributeCondition {
  statements: AttributeStatement[];
  relationToNext: LogicRelation;
}

export type Branch =
  | EventBranch
  | MaxTimeBranch
  | MultisplitBranch
  | ExperimentBranch
  | ResponseBranch;

export interface AiResponseNodeData extends CommonNodeData {
  type: NodeType.AI_RESPONSE;
  channel?: "email" | "slack" | "sms";
  connectionId?: string;
  subject?: string;
  prompt?: string;
  agentId?: string;
  model?: string;
  maxTurns?: number;
  replyTimeoutMinutes?: number;
  waitForCustomerReplyBeforeFirstResponse?: boolean;
  replyOnly?: boolean;
  onComplete?: "advance" | "exit";
}

export type RicochetAction =
  | "updateStatus"
  | "addTag"
  | "createTask"
  | "createNote"
  | "getTask"
  | "getTasks"
  | "getNotes";

export interface RicochetNodeData extends CommonNodeData {
  type: NodeType.RICOCHET;
  action?: RicochetAction;
  statusId?: string;
  statusName?: string;
  tagName?: string;
  tagNames?: string[];
  skipIfBlank?: boolean;
  taskId?: string;
  taskName?: string;
  taskDescription?: string;
  taskPriority?: string;
  taskInvitees?: string;
  taskReminderDate?: string;
  taskReminderTime?: string;
  taskReminderPopup?: boolean;
  taskReminderEmail?: boolean;
  note?: string;
}

export interface Stats {
  sent?: number;
  delivered?: number;
  clickedPercentage?: number;
  wssent?: number;
  openedPercentage?: number;
  failed?: number;
  replied?: number;
}

export interface CommonNodeData {
  stepId?: string;
  stats?: Stats;
  showErrors?: boolean;
  disabled?: boolean;
  customersCount?: number;
}

export type MessageTemplatePushbuilderData = PushBuilderData & {
  selectedPlatform: "All" | PushPlatforms;
};

export interface MessageNodeData<T extends MessageType = MessageType>
  extends CommonNodeData {
  type: NodeType.MESSAGE | NodeType.PUSH;
  customName?: string;
  recipientMode?: "customer" | "internal";
  alsoNotifyInternal?: boolean;
  internalRecipientGroupIds?: string[];
  additionalEmails?: string[];
  dynamicRecipientSources?: string[];
  additionalPhoneNumbers?: string[];
  template: {
    type: T;
    selected?: {
      id: number;
      name: string;
      pushBuilder?: MessageTemplatePushbuilderData;
    };
  };
  oneClickUnsubscribeEnabled: boolean;
  connectionId?: string;
  smsSenderMode?: "connection" | "agency";
  sendingOptionId?: string;
  replyToOptionId?: string;
  oneClickUnsubscribeOptionId?: string;
  // Per-step email content edited inside this workflow. Lets a user tweak the
  // wording (and pick a style) without touching the shared template the node
  // points at. When present, the send path uses `html`/`css` instead of the
  // template's body. `body` + `styleKey` are kept so the inline editor can
  // re-open and re-compose.
  emailOverride?: {
    body?: string;
    styleKey?: string;
    html?: string;
    css?: string;
  };
  // Per-step SMS/Slack message text edited inside this workflow. When present,
  // the send path uses it instead of the shared template's text, leaving the
  // template untouched.
  textOverride?: string;
}

export interface WaitUntilNodeData extends CommonNodeData {
  type: NodeType.WAIT_UNTIL;
  branches: WaitUntilBranch[];
}

export interface MultisplitNodeData extends CommonNodeData {
  type: NodeType.MULTISPLIT;
  branches: MultisplitBranch[];
}

export interface ExperimentNodeData extends CommonNodeData {
  type: NodeType.EXPERIMENT;
  branches: ExperimentBranch[];
  winnerBranchId?: string | null;
  autoWinner?: ExperimentAutoWinnerSettings;
}

export interface ResponseBranchNodeData extends CommonNodeData {
  type: NodeType.RESPONSE_BRANCH;
  branches: ResponseBranch[];
  /**
   * Top-level routing strategy. New nodes default to "ai". Nodes saved
   * before this field existed are treated as "keyword" by the matcher.
   */
  routingMode?: ResponseRoutingMode;
  /** When true, the AI is asked to classify replies that don't keyword-match (keyword mode only) */
  aiFallback: boolean;
  /** Optional OpenRouter model override; defaults to AI_RESPONSE_BRANCH_MODEL, then AI_BUILDER_MODEL on the server */
  aiModel?: string;
}

export interface TimeDelayNodeData extends CommonNodeData {
  type: NodeType.TIME_DELAY;
  delay: DelayData;
}

export enum TimeWindowTypes {
  SPEC_DATES = "SpecDates",
  SPEC_WEEK_DAYS = "SpecWeekDays",
}

export interface TimeWindowNodeData extends CommonNodeData {
  type: NodeType.TIME_WINDOW;
  windowType?: TimeWindowTypes;
  onDays?: number[];
  fromTime?: string;
  toTime?: string;
  from?: string;
  to?: string;
}

export interface JumpToNodeData extends CommonNodeData {
  type: NodeType.JUMP_TO;
  targetId?: string;
}

export enum TrackerVisibility {
  SHOW = "show",
  HIDE = "hide",
}

export interface TrackerNodeData extends CommonNodeData {
  type: NodeType.TRACKER;
  needsCheck?: boolean;
  tracker?: {
    trackerId: string;
    trackerTemplate: { id: number; name: string };
    visibility: TrackerVisibility;
    fields: { name: string; type: StatementValueType; value: string }[];
  };
}

export interface AnotherNodeData extends CommonNodeData {
  type?: Exclude<
    NodeType,
    | NodeType.MESSAGE
    | NodeType.PUSH
    | NodeType.WAIT_UNTIL
    | NodeType.TIME_DELAY
    | NodeType.TIME_WINDOW
    | NodeType.USER_ATTRIBUTE
    | NodeType.JUMP_TO
    | NodeType.TRACKER
    | NodeType.MULTISPLIT
    | NodeType.EXPERIMENT
    | NodeType.AI_RESPONSE
    | NodeType.RESPONSE_BRANCH
    | NodeType.RICOCHET
  >;
}

export type NodeData =
  | MessageNodeData
  | WaitUntilNodeData
  | TimeDelayNodeData
  | TimeWindowNodeData
  | JumpToNodeData
  | MultisplitNodeData
  | ExperimentNodeData
  | TrackerNodeData
  | AiResponseNodeData
  | ResponseBranchNodeData
  | RicochetNodeData
  | AnotherNodeData;
